import { useEffect, useRef } from "react";
import { useEmbed } from "./useEmbed";

/**
 * Hook that communicates the page height to the parent window when in embed mode.
 * This allows the parent to resize the iframe dynamically to fit content.
 */
export const useEmbedResize = () => {
  const { isEmbedded } = useEmbed();
  const lastHeightRef = useRef<number>(0);

  useEffect(() => {
    if (!isEmbedded) return;

    const sendHeight = () => {
      // Get the full document height
      const height = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );

      // Only send if height actually changed (avoid spam)
      if (height !== lastHeightRef.current) {
        lastHeightRef.current = height;

        window.parent.postMessage(
          {
            type: "laborhack-embed-resize",
            height: height,
          },
          "*" // In production, you might want to restrict this to known parent origins
        );
      }
    };

    // Send initial height
    sendHeight();

    // Watch for DOM changes that might affect height
    const resizeObserver = new ResizeObserver(() => {
      sendHeight();
    });

    resizeObserver.observe(document.body);

    // Also watch for mutations (dynamic content loading)
    const mutationObserver = new MutationObserver(() => {
      // Debounce slightly to batch rapid changes
      requestAnimationFrame(sendHeight);
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Handle window resize
    window.addEventListener("resize", sendHeight);

    // Send height periodically for any edge cases (every 500ms)
    const intervalId = setInterval(sendHeight, 500);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", sendHeight);
      clearInterval(intervalId);
    };
  }, [isEmbedded]);
};
